import { ThreemaUpdateInput } from '@bp/bp-graphql-types';
import { Button, Modal, ProfileIcon, showToast, Table, TableColumns, useDefaultSorting } from '@bp/ui-components';
import { DocumentType, FragmentType, getFragmentData, graphql } from 'client/gql';
import { ThreemaProps } from 'client/gql/graphql';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { showSuccessToast } from 'utils/toasts';

export const ThreemaAssignModalFragment = graphql(`
  fragment ThreemaAssignModal on Query {
    profiles(
      where: {
        organization: { uuid: $organizationUuid }
        threemaConnection_NONE: { edge: { NOT: { threemaId: null } } }
      }
    ) {
      uuid
      __typename
      email
      firstName
      lastName
      threemaConnection {
        __typename
        edges {
          properties {
            __typename
            threemaId
          }
        }
      }
    }
  }
`);

const AssignThreemaToProfile = graphql(`
  mutation AssignThreemaToProfile($update: ThreemaUpdateInput!) {
    updateThreemas(update: $update) {
      threemas {
        uuid
        __typename
        profiles {
          uuid
          __typename
          email
          firstName
          lastName
          threemaConnection {
            __typename
            edges {
              properties {
                __typename
                threemaId
              }
            }
          }
        }
      }
    }
  }
`);

type ThreemaAssignModalTableType = DocumentType<typeof ThreemaAssignModalFragment>['profiles'][0];

type ThreemaAssignModalProps = {
  query: FragmentType<typeof ThreemaAssignModalFragment> | undefined;
  isOpen: boolean;
  onRequestClose: () => void;
  threemaDetails: ThreemaProps | null;
};

export const ThreemaAssignModal = ({ query, isOpen, onRequestClose, threemaDetails }: ThreemaAssignModalProps) => {
  const { t } = useTranslation();
  const fragment = getFragmentData(ThreemaAssignModalFragment, query);
  const [{ fetching }, mutation] = useMutation(AssignThreemaToProfile);
  const data = fragment?.profiles ?? [];

  const columns: TableColumns<ThreemaAssignModalTableType>[] = [
    {
      header: t('common.firstName'),
      id: 'firstName',
      accessorKey: 'firstName',
    },
    {
      header: t('common.lastName'),
      id: 'lastName',
      accessorKey: 'lastName',
    },
    {
      header: t('common.email'),
      id: 'email',
      accessorKey: 'email',
    },
  ];

  const { sorting, onSortingChange } = useDefaultSorting();

  const handleAssignClick = async (profileInput: ThreemaAssignModalTableType) => {
    try {
      const update: ThreemaUpdateInput = {
        profiles: [
          {
            connect: [
              {
                edge: {
                  ...threemaDetails,
                  enabled: true,
                },
                where: { node: { uuid: profileInput.uuid } },
              },
            ],
          },
        ],
      };
      await mutation({ update });
      showSuccessToast(t('threema.assign_success'));
      onRequestClose();
    } catch (error) {
      showToast(t('threema.assign_error'), {
        type: 'error',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} title={t('threema.assign_title')}>
      <Table<ThreemaAssignModalTableType>
        breakpoint='580px'
        data-cy='threemaList'
        showActionBar
        isOnWhite={false}
        showBorderRadius
        showShadow
        showSort
        canScroll
        sorting={sorting}
        onSortingChange={onSortingChange}
        actionBarSettings={{
          showExpertFilter: true,
        }}
        emptyStateSettings={{
          icon: <ProfileIcon />,
          padding: 'xl',
        }}
        columns={columns}
        data={data}
        lastColWidth='100px'
        lastCol={(row) => {
          return (
            <Button
              hierarchy='primary'
              onClick={() => handleAssignClick(row.original)}
              fullWidth
              isLoading={fetching}
              disabled={!threemaDetails}
            >
              {t('threema.assign')}
            </Button>
          );
        }}
      />
    </Modal>
  );
};
